// Contestação / feedback da avaliação da IA (Memor.IA).
// Botão "Não concordo / dar feedback" que abre um modal para o operador explicar por que
// discorda da nota (sugerindo a nota justa) ou comentar a análise. Mostra o histórico da
// reunião com o status (aberta/aceita/mantida) e a resposta do gestor.
// Backend: core/views_memora_contestacao.py
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE;
const MOTIVO_MIN = 10;
const MOTIVO_MAX = 2000;

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } });

export const STATUS_CONTESTACAO = {
  aberta: { label: 'Aguardando gestor', cls: 'bg-warning text-dark' },
  aceita: { label: 'Aceita', cls: 'bg-success' },
  mantida: { label: 'Avaliação mantida', cls: 'bg-secondary' },
};

export const TIPO_CONTESTACAO = {
  discordo: 'Não concordo com a avaliação',
  feedback: 'Feedback sobre a avaliação',
};

const fmtNota = (n) => (n === null || n === undefined || n === '' ? '-' : Number(n).toLocaleString('pt-BR', { maximumFractionDigits: 1 }));

export default function ContestacaoAnalise({ codigoReuniao, notaIa, compact }) {
  const [aberto, setAberto] = useState(false);
  const [tipo, setTipo] = useState('discordo');
  const [notaSugerida, setNotaSugerida] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState(null); // { tipo: 'success'|'danger', texto }
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(async () => {
    if (!codigoReuniao) return;
    setCarregando(true);
    try {
      const r = await axios.get(`${API}/api/reunioes/contestacoes/`, {
        ...authHeader(),
        params: { codigo: codigoReuniao },
      });
      setHistorico(r.data?.itens || []);
    } catch (e) {
      setHistorico([]);
    } finally {
      setCarregando(false);
    }
  }, [codigoReuniao]);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  if (!codigoReuniao) return null;

  const fechar = () => {
    setAberto(false);
    setMsg(null);
  };

  const motivoLen = motivo.trim().length;
  const notaInvalida = tipo === 'discordo' && notaSugerida !== '' &&
    (Number.isNaN(Number(notaSugerida)) || Number(notaSugerida) < 0 || Number(notaSugerida) > 10);
  const podeEnviar = !enviando && motivoLen >= MOTIVO_MIN && motivoLen <= MOTIVO_MAX && !notaInvalida;

  const enviar = async () => {
    if (!podeEnviar) return;
    setEnviando(true);
    setMsg(null);
    try {
      await axios.post(`${API}/api/reunioes/contestacao/`, {
        codigo_reuniao: codigoReuniao,
        tipo,
        nota_sugerida: tipo === 'discordo' && notaSugerida !== '' ? Number(notaSugerida) : null,
        motivo: motivo.trim(),
      }, authHeader());
      setMsg({ tipo: 'success', texto: 'Enviado. O gestor vai analisar e responder por aqui.' });
      setMotivo('');
      setNotaSugerida('');
      carregar();
    } catch (e) {
      setMsg({ tipo: 'danger', texto: e.response?.data?.erro || 'Não foi possível enviar. Tente novamente.' });
    } finally {
      setEnviando(false);
    }
  };

  const modal = (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1300 }}
      onClick={(e) => { e.stopPropagation(); fechar(); }}
    >
      <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-comment-dots me-2"></i>Avaliação da IA
              </h5>
              <small style={{ color: '#6c757d' }}>
                Reunião {codigoReuniao}{notaIa !== undefined && notaIa !== null && notaIa !== '' ? ` · nota da IA: ${fmtNota(notaIa)}/10` : ''}
              </small>
            </div>
            <button type="button" className="btn-close" aria-label="Fechar" onClick={fechar}></button>
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label fw-semibold mb-1">O que você quer registrar?</label>
              {Object.entries(TIPO_CONTESTACAO).map(([valor, label]) => (
                <div className="form-check" key={valor}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`tipo-contestacao-${codigoReuniao}`}
                    id={`tipo-${valor}-${codigoReuniao}`}
                    checked={tipo === valor}
                    onChange={() => setTipo(valor)}
                  />
                  <label className="form-check-label" htmlFor={`tipo-${valor}-${codigoReuniao}`}>
                    {valor === 'feedback' ? 'Quero dar um feedback sobre a avaliação' : label}
                  </label>
                </div>
              ))}
            </div>

            {tipo === 'discordo' && (
              <div className="mb-3" style={{ maxWidth: '220px' }}>
                <label className="form-label fw-semibold mb-1" htmlFor={`nota-sug-${codigoReuniao}`}>
                  Nota que eu acho justa <span className="text-muted fw-normal">(opcional)</span>
                </label>
                <input
                  id={`nota-sug-${codigoReuniao}`}
                  type="number"
                  className={`form-control ${notaInvalida ? 'is-invalid' : ''}`}
                  min="0"
                  max="10"
                  step="0.5"
                  value={notaSugerida}
                  onChange={(e) => setNotaSugerida(e.target.value)}
                  placeholder="0 a 10"
                />
                {notaInvalida && <div className="invalid-feedback">Informe uma nota entre 0 e 10.</div>}
              </div>
            )}

            <div className="mb-2">
              <label className="form-label fw-semibold mb-1" htmlFor={`motivo-${codigoReuniao}`}>Por quê?</label>
              <textarea
                id={`motivo-${codigoReuniao}`}
                className="form-control"
                rows={4}
                maxLength={MOTIVO_MAX}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Explique o que a IA não considerou ou o que você acha que ficou injusto."
              />
              <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.8rem' }}>
                <span className={motivoLen > 0 && motivoLen < MOTIVO_MIN ? 'text-danger' : 'text-muted'}>
                  Mínimo de {MOTIVO_MIN} caracteres
                </span>
                <span className="text-muted">{motivoLen}/{MOTIVO_MAX}</span>
              </div>
            </div>

            {msg && (
              <div className={`alert alert-${msg.tipo} py-2 px-3 mb-2`} style={{ fontSize: '0.875rem' }}>
                {msg.texto}
              </div>
            )}

            <div className="d-flex justify-content-end mb-3">
              <button type="button" className="btn btn-primary" onClick={enviar} disabled={!podeEnviar}>
                {enviando
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                  : <><i className="fas fa-paper-plane me-2"></i>Enviar</>}
              </button>
            </div>

            <hr className="my-3" />
            <h6 className="fw-bold mb-2" style={{ color: '#212529' }}>
              <i className="fas fa-history me-2 text-muted"></i>Histórico desta reunião
            </h6>
            {carregando ? (
              <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                <span className="spinner-border spinner-border-sm me-2"></span>Carregando...
              </div>
            ) : historico.length === 0 ? (
              <div className="text-muted" style={{ fontSize: '0.875rem' }}>Nenhuma contestação ou feedback registrado.</div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {historico.map((h) => {
                  const st = STATUS_CONTESTACAO[h.status] || STATUS_CONTESTACAO.aberta;
                  return (
                    <div key={h.id} className="border rounded p-2" style={{ fontSize: '0.875rem' }}>
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
                        <span className="fw-semibold">{TIPO_CONTESTACAO[h.tipo] || h.tipo}</span>
                        <span className={`badge ${st.cls}`}>{st.label}</span>
                      </div>
                      <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>
                        {h.created_at}
                        {h.operador && ` · ${h.operador}`}
                        {h.tipo === 'discordo' && ` · nota da IA ${fmtNota(h.nota_ia)}`}
                        {h.nota_sugerida !== null && h.nota_sugerida !== undefined && ` → sugerida ${fmtNota(h.nota_sugerida)}`}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{h.motivo}</div>
                      {h.resposta && (
                        <div className="mt-2 p-2 rounded" style={{ background: '#f8f9fa', borderLeft: '3px solid #6c757d' }}>
                          <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>
                            <i className="fas fa-reply me-1"></i>Resposta{h.respondido_por ? ` de ${h.respondido_por}` : ''}{h.respondido_em ? ` · ${h.respondido_em}` : ''}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{h.resposta}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        type="button"
        className={`btn btn-danger ${compact ? 'btn-sm' : ''}`}
        onClick={(e) => { e.stopPropagation(); setAberto(true); }}
      >
        <i className="fas fa-comment-dots me-2"></i>Não concordo / dar feedback
      </button>
      {aberto && ReactDOM.createPortal(modal, document.body)}
    </>
  );
}
